import Snackbar, { SnackbarProps } from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SnackbarContent from '@mui/material/SnackbarContent';
import { Colors } from 'styles';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteForever from '@mui/icons-material/DeleteForever';
import { forwardRef, SyntheticEvent } from 'react';

const RedWarning: React.ReactNode = <WarningIcon style={{ color: `${Colors.red6}` }} />;
const RedDeleteForever: React.ReactNode = <DeleteForever style={{ color: `${Colors.red6}` }} />;
const YellowWarning: React.ReactNode = <WarningIcon style={{ color: `${Colors.yellow5}` }} />;
const GreenCheck: React.ReactNode = <CheckCircleIcon style={{ color: `${Colors.green6}` }} />;

const severityColorsMap = {
  success: Colors.green4,
  error: Colors.red4,
  info: Colors.blue4,
  default: Colors.gray5,
} as const;

const iconsMap = {
  redWarning: RedWarning,
  yellowWarning: YellowWarning,
  greenCheck: GreenCheck,
  redDeleteForever: RedDeleteForever,
} as const;

export type SnackBarIcon = keyof typeof iconsMap;

export type SnackbarContentProps = {
  action: React.ReactNode;
  message: React.ReactNode;
  role: string;
  reduceCloseButtonHeight?: boolean;
};

export type SnackbarContentClassKey = 'root' | 'message' | 'action';

export type SnackbarCloseReason = 'timeout' | 'clickaway' | 'escapeKeyDown';

export type SnackbarCloseEvent = Event | SyntheticEvent;

export type GenericSnackBarProps = {
  /**
   * Message to show in snack bar
   */
  msg: SnackbarContentProps['message'];
  /**
   * Name of snack bar action
   */
  actionName?: React.ReactNode;
  /**
   * Function to invoke on action click
   */
  actionHandler?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  /**
   * Color of the action button
   */
  actionColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  /**
   * Snackbar severity
   */
  severity?: keyof typeof severityColorsMap;
  /**
   * Icon to display at the left of the SnackBar, before the message
   */
  icon?: SnackBarIcon;
  /**
   * Optional string to use for a data-testid property on the snackbar
   */
  ['data-testid']?: string;
  /**
   * The action to display. It renders after the message, at the end of the snackbar.
   */
  action?: SnackbarContentProps['action'];
  /**
   * The vertical anchor of the `Snackbar`.
   */
  vertical?: 'top' | 'bottom';
  /**
   * The horizontal anchor of the `Snackbar`.
   */
  horizontal?: 'left' | 'center' | 'right';
  /**
   * The number of milliseconds to wait before automatically calling the
   * `onClose` function. `onClose` should then set the state of the `open`
   * prop to hide the Snackbar. This behavior is disabled by default with
   * the `null` value.
   */
  duration?: number | null;
  autoHideDuration?: number | null;
  /**
   * Replace the `SnackbarContent` component.
   */
  children?: React.ReactNode;
  /**
   * Props applied to the `ClickAwayListener` element.
   */
  ClickAwayListenerProps?: SnackbarProps['ClickAwayListenerProps'];
  /**
   * Props applied to the [`SnackbarContent`](/api/snackbar-content/) element.
   */
  ContentProps?: Partial<SnackbarContentProps>;
  /**
   * If `true`, the `autoHideDuration` timer will expire even if the window is not focused.
   */
  disableWindowBlurListener?: boolean;
  /**
   * When displaying multiple consecutive Snackbars from a parent rendering a single
   * <Snackbar/>, add the key prop to ensure independent treatment of each message.
   * e.g. <Snackbar key={message} />, otherwise, the message may update-in-place and
   * features such as autoHideDuration may be canceled.
   * @document
   */
  key?: string;
  /**
   * The message to display.
   */
  message?: SnackbarContentProps['message'];
  /**
   * Callback fired when the component requests to be closed.
   * Typically `onClose` is used to set state in the parent component,
   * which is used to control the `Snackbar` `open` prop.
   * The `reason` parameter can optionally be used to control the response to `onClose`,
   * for example ignoring `clickaway`.
   *
   * @param {object} event The event source of the callback.
   * @param {string} reason Can be: `"timeout"` (`autoHideDuration` expired), `"clickaway"`.
   */
  handleClose?: SnackbarProps['onClose'];
  /**
   * Callback fired before the transition is entering.
   */
  onEnter?: () => void;
  /**
   * Callback fired when the transition has entered.
   */
  onEntered?: () => void;
  /**
   * Callback fired when the transition is entering.
   */
  onEntering?: () => void;
  /**
   * Callback fired before the transition is exiting.
   */
  onExit?: () => void;
  /**
   * Callback fired when the transition has exited.
   */
  onExited?: () => void;
  /**
   * Callback fired when the transition is exiting.
   */
  onExiting?: () => void;
  /**
   * Callback fired when the mouse enters the snackbar
   */
  onMouseEnter?: (arg1: React.SyntheticEvent<HTMLElement>) => void;
  /**
   * Callback fired when the mouse leaves the snackbar
   */
  onMouseLeave?: (arg1: React.SyntheticEvent<HTMLElement>) => void;

  /**
   * If `true`, `Snackbar` is open.
   */
  open?: boolean;
  /**
   * The number of milliseconds to wait before dismissing after user interaction.
   * If `autoHideDuration` prop isn't specified, it does nothing.
   * If `autoHideDuration` prop is specified but `resumeHideDuration` isn't,
   * we default to `autoHideDuration / 2` ms.
   */
  resumeHideDuration?: number;
};

export type GenericSnackBarContentProps = Readonly<{
  /**
   * Message to show in snack bar
   */
  message: SnackbarContentProps['message'];

  /**
   * Snackbar severity
   */
  severity?: keyof typeof severityColorsMap;
  /**
   * Name of snack bar action
   */
  actionName?: React.ReactNode;
  /**
   * Function to invoke on action click
   */
  actionHandler?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  /**
   * Color of the action button
   */
  actionColor?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'info'
    | 'warning'
    | string;
  /**
   * Icon to display at the left of the SnackBar, before the message
   */
  icon?: SnackBarIcon;
  /**
   * Callback fired when the component requests to be closed.
   * Typically `onClose` is used to set state in the parent component,
   * which is used to control the `Snackbar` `open` prop.
   * The `reason` parameter can optionally be used to control the response to `onClose`,
   * for example ignoring `clickaway`.
   *
   * @param {object} event The event source of the callback.
   * @param {string} reason Can be: `"timeout"` (`autoHideDuration` expired), `"clickaway"`.
   */
  onClose?: SnackbarProps['onClose'];
  action?: React.ReactNode;
  role?: string;
  reduceCloseButtonHeight?: boolean;
}>;

export const GenericSnackbarContent = forwardRef<HTMLDivElement, GenericSnackBarContentProps>(
  function GenericSnackbarContent(
    {
      severity = 'default',
      message,
      icon,
      actionName = null,
      actionHandler,
      onClose,
      actionColor = 'primary',
      reduceCloseButtonHeight = false,
      ...rest
    },
    ref
  ) {
    return (
      <SnackbarContent
        ref={ref}
        sx={{
          backgroundColor: severityColorsMap[severity],
        }}
        message={
          <span
            id="message-id"
            style={{
              color: 'white',
              maxWidth: 550,
              display: icon != null ? 'flex' : 'inline-block',
              alignItems: icon != null ? 'center' : undefined,
            }}
          >
            {icon != null ? (
              <>
                <span style={{ display: 'inline-flex', marginRight: 16 }}>{iconsMap[icon]}</span>
                {message}
              </>
            ) : (
              message
            )}
          </span>
        }
        action={[
          actionName != null ? (
            <Button
              key="actionButton"
              color={
                actionColor as
                  | 'inherit'
                  | 'primary'
                  | 'secondary'
                  | 'success'
                  | 'error'
                  | 'info'
                  | 'warning'
              }
              size="small"
              onClick={actionHandler}
              sx={{ color: actionColor === 'info' ? Colors.gray10 : undefined }}
            >
              {actionName}
            </Button>
          ) : null,
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            data-testid="snack-bar--close-button"
            onClick={(e) => onClose?.(e, 'timeout')}
            sx={{
              p: 2,
              ...(reduceCloseButtonHeight ? { pt: '10px', pb: '10px' } : null),
            }}
            size="large"
          >
            <CloseIcon sx={{ color: 'white' }} />
          </IconButton>,
        ]}
        {...rest}
      />
    );
  }
);

function GenericSnackBar({
  msg,
  icon,
  actionName = null,
  actionHandler,
  duration = 4000,
  vertical = 'top',
  horizontal = 'right',
  handleClose,
  open,
  severity = 'success',
  'data-testid': testId,
  actionColor,
  ContentProps,
  ...rest
}: GenericSnackBarProps): React.ReactElement {
  return (
    <Snackbar
      anchorOrigin={{
        vertical,
        horizontal,
      }}
      open={open}
      autoHideDuration={duration}
      onClose={handleClose}
      data-testid={testId}
      sx={{
        top: 'env(titlebar-area-height, 15px) !important',
      }}
      {...rest}
    >
      <GenericSnackbarContent
        severity={severity}
        message={msg}
        icon={icon}
        actionName={actionName}
        actionHandler={actionHandler}
        actionColor={actionColor}
        onClose={handleClose}
        {...ContentProps}
      />
    </Snackbar>
  );
}

export default GenericSnackBar;
